import { useNavigate, useParams } from 'react-router-dom';
import { useGetProfilesQuery } from '../../store/backendApi';
import { EditTripHeader } from './editTripHeader';
import { EditTripSidebar } from './editTripSidebar';
import { EditStoryModal } from './story/editStoryModal';
import { useEditTripData } from '../../hooks/editTrip/useEditTripData';
import { usePublishTrip } from '../../hooks/editTrip/usePublishTrip';
import { useEditStoryModal } from '../../hooks/editTrip/useEditStoryModal';
import { useEditSidebar } from '../../hooks/editTrip/useEditSidebar';
import { useStoryOperations } from '../../hooks/editTrip/useStoryOperations';
import { useTripNavigation } from '../../hooks/editTrip/useTripNavigation';
import { EditTripActions } from './EditTripActions';
import { EditTripSettingsMenu } from './EditTripSettingsMenu';
import { EditStoryList } from './EditStoryList';

/**
 * Edit trip page component - refactored to separate business logic from UI.
 * All business logic is now in custom hooks, making it reusable for React Native.
 */
export const EditTripPage = () => {
  const params = useParams();
  const tripId = params['tripId'];
  const navigate = useNavigate();
  const { data: user } = useGetProfilesQuery();

  // Business logic hooks (platform-agnostic)
  const { trip, stories, error } = useEditTripData(tripId);
  const { publish, isPublishing, hasUnpublishedChanges } = usePublishTrip(
    tripId,
    trip?.unpublishedChanges,
  );
  const { viewTripUrl } = useTripNavigation(trip, user?.username, tripId);
  const { createStory, deleteStory, isCreatingStory } = useStoryOperations(tripId);

  // UI state hooks
  const storyModal = useEditStoryModal();
  const sidebar = useEditSidebar();

  // Handlers
  const handleAddStory = async () => {
    const newStory = await createStory();
    if (newStory) {
      storyModal.openForCreate(newStory);
    }
  };

  const handleViewTrip = () => {
    if (viewTripUrl) {
      navigate(viewTripUrl);
    }
  };

  // Error handling
  if (error) {
    return <p>{error}</p>;
  }

  return (
    <div className="relative bg-canvas min-h-screen">
      <div className="relative">
        <EditTripHeader tripData={trip} />
        <EditTripSettingsMenu
          onOpenDetails={() => sidebar.open('trip-details')}
          onOpenSharing={() => sidebar.open('privacy-sharing')}
        />
      </div>

      <EditTripActions
        hasUnpublishedChanges={hasUnpublishedChanges}
        isPublishing={isPublishing}
        onPublish={publish}
        onViewTrip={handleViewTrip}
      />

      <EditStoryModal
        story={storyModal.editingStory}
        isOpen={storyModal.isOpen}
        onClose={storyModal.close}
      />

      <EditTripSidebar tripData={trip} section={sidebar.activeSection} onClose={sidebar.close} />

      <EditStoryList
        stories={stories ?? []}
        isCreatingStory={isCreatingStory}
        onAddStory={handleAddStory}
        onEditStory={storyModal.openForEdit}
        onDeleteStory={deleteStory}
      />

      <footer className="bg-canvas">
        <div className="mx-auto max-w-7xl px-6 py-12 md:flex md:items-center md:justify-between lg:px-8">
          <p className="mt-8 text-center text-sm/6 text-muted md:order-1 md:mt-0">
            All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};
